import { routerNavigatedAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import { createEmptyStateResource, createStateResource, StateResource } from '@schir-int-client/ngrx-helpers';
import { loadVerfahrenSingleSuccessAction } from '@schir-int-client/verfahren-shared';
import {
	createKorrespondenzVerfuegungByVorgangAction,
	createKorrespondenzVerfuegungByVorgangSuccessAction,
	createVerfuegungByVorgangAction,
	createVerfuegungByVorgangSuccessAction,
	deleteVerfuegungSuccessAction,
	loadVerfuegungenByVorgangAction,
	loadVerfuegungenByVorgangSuccessAction,
	loadVerfuegungSuccessAction,
	setSelectedVerfuegungAction,
	updateVerfuegungNotizSuccessAction,
} from './verfuegung.actions';
import { VerfuegungListResource, VerfuegungResource } from './verfuegung.model';
import { Resource } from '@ngxp/rest';
import { loadVorgangSuccessAction } from '@schir-int-client/vorgang-shared';
import { sameUri } from '@schir-int-client/tech';

export interface VerfuegungState {
	selectedVerfuegung: StateResource<VerfuegungResource>;
	verfuegungen: StateResource<VerfuegungListResource>;
}

export const initialState: VerfuegungState = {
	selectedVerfuegung: createEmptyStateResource(),
	verfuegungen: createEmptyStateResource(),
};

let uriVorgang: string;

export const verfuegungReducer = createReducer(
	initialState,
	on(
		setSelectedVerfuegungAction,
		loadVerfuegungSuccessAction,
		(state: VerfuegungState, { verfuegung }): VerfuegungState => {
			if (
				state.selectedVerfuegung &&
				state.selectedVerfuegung.resource &&
				verfuegung._links.self.href === state.selectedVerfuegung.resource._links.self.href
			) {
				return state;
			}
			return {
				...state,
				selectedVerfuegung: createStateResource(verfuegung),
			};
		}
	),
	on(loadVerfuegungenByVorgangAction, (state: VerfuegungState): VerfuegungState => ({
		...state,
		verfuegungen: { ...state.verfuegungen, loading: true },
	})),
	on(loadVerfuegungenByVorgangSuccessAction, (state: VerfuegungState, { verfuegungen }): VerfuegungState => {
		const selectedVerfuegung = state.selectedVerfuegung.resource;
		if (verfuegungen._embedded) {
			const verfuegungList = <VerfuegungResource[]>verfuegungen._embedded['verfuegungList'];
			//set first verfuegung as selected if the selected verfuegung is from other vorgang or none has been selected before
			if (!selectedVerfuegung || !belongsToCurrentVorgang(verfuegungList, selectedVerfuegung)) {
				return {
					...state,
					selectedVerfuegung: createStateResource(verfuegungList[0]),
					verfuegungen: createStateResource(verfuegungen),
				};
			}
		}
		return {
			...state,
			verfuegungen: createStateResource(verfuegungen),
		};
	}),
	on(createVerfuegungByVorgangAction, (state: VerfuegungState): VerfuegungState => ({
		...state,
		selectedVerfuegung: { ...state.selectedVerfuegung, loading: true },
	})),
	on(createKorrespondenzVerfuegungByVorgangAction, (state: VerfuegungState): VerfuegungState => ({
		...state,
		selectedVerfuegung: { ...state.selectedVerfuegung, loading: true },
	})),
	on(createVerfuegungByVorgangSuccessAction, (state: VerfuegungState, { verfuegung }): VerfuegungState => ({
		...state,
		verfuegungen: { ...state.verfuegungen, reload: true },
		selectedVerfuegung: createStateResource(verfuegung),
	})),
	on(deleteVerfuegungSuccessAction, (state: VerfuegungState, { verfuegungen, deleted }): VerfuegungState => {
		if (state.selectedVerfuegung.resource?._links.self.href == deleted?._links.self.href) {
			return {
				...state,
				verfuegungen: createStateResource(verfuegungen),
				selectedVerfuegung: createStateResource(verfuegungen?._embedded?.['verfuegungList'][0]),
			};
		}
		return {
			...state,
			verfuegungen: createStateResource(verfuegungen),
		};
	}),
	on(
		createKorrespondenzVerfuegungByVorgangSuccessAction,
		(state: VerfuegungState, { verfuegung }): VerfuegungState => ({
			...state,
			verfuegungen: { ...state.verfuegungen, reload: true },
			selectedVerfuegung: createStateResource(verfuegung),
		})
	),
	on(loadVerfahrenSingleSuccessAction, (state: VerfuegungState, { verfahren, predecessor }): VerfuegungState => {
		//don't change the selection if the verfahren is loaded for the first time or the same verfahren is loaded again
		if (!predecessor || (verfahren && predecessor && predecessor.aktenzeichen === verfahren.aktenzeichen)) {
			return {
				...state,
				verfuegungen: createEmptyStateResource(),
			};
		}
		//else remove the selection
		return {
			...state,
			verfuegungen: createEmptyStateResource(),
			selectedVerfuegung: createEmptyStateResource(),
		};
	}),
	on(loadVorgangSuccessAction, (state: VerfuegungState): VerfuegungState => ({
		...state,
		verfuegungen: createEmptyStateResource(),
		selectedVerfuegung: createEmptyStateResource(),
	})),
	on(updateVerfuegungNotizSuccessAction, (state: VerfuegungState, { verfuegung }): VerfuegungState => ({
		...state,
		selectedVerfuegung: createStateResource(verfuegung),
		verfuegungen: { ...state.verfuegungen, reload: true },
	})),
	on(routerNavigatedAction, (state: VerfuegungState, { payload }): VerfuegungState => {
		const lastUriVorgang = uriVorgang;
		const newUri: RegExpExecArray = /vorgang:[^)]*/g.exec(payload.event.url);
		uriVorgang = newUri ? newUri[0] : 'none';
		return lastUriVorgang === uriVorgang
			? state
			: {
					...state,
					verfuegungen: createEmptyStateResource(),
				};
	})
);

//checks if the selected verfuegung is part of the verfuegungen of the current vorgang
function belongsToCurrentVorgang(verfuegungList: Resource[], selectedVerfuegung: VerfuegungResource): boolean {
	return verfuegungList.some(verfuegung => sameUri(selectedVerfuegung, verfuegung));
}
